/** 组合 */
import { FC, ReactNode, Fragment, } from 'react';
import classNames from 'classnames';
import { ProInputNumber, ProInput, ProSelect, } from '../antd';
import FormItem from './subpage/formItem';
import SearchFormItem from './subpage/searchFormItem';
import SearchFormDisabled from './subpage/disabled';
import {
  BaseSearchFormItemProps,
  SearchFormItemEnum,
} from './interface/baseInterface';
import { IEnumItem, useLayoutContext } from '../PageLayout/context/LayoutContext';
import { InputProps } from 'antd/lib/input';
import { InputNumberProps } from 'antd/lib/input-number';
import { SelectProps } from 'antd/lib/select';
import { FormItemProps } from 'antd/lib/form';
import { TABLE_COLUMN_EMPTY } from '../constants/constant';
import { arraySum } from '../utils/utils';
import { getInputPlaceholder, getSelectPlaceholder } from '../utils/ruleUtils';
import { renderEnumName } from '../utils/renderUtils';

export interface IBaseItem {
  /** 表单配置 */
  formItemProps?: FormItemProps;
  /** 标题 */
  label?: ReactNode;
  /** 标题宽度 */
  labelWidth?: number;
  /** flex多少等分 */
  flex?: number;
  /** 枚举值 */
  options?: IEnumItem[];
  /** 单位 */
  company?: ReactNode;
  /** 单位宽度 */
  companyWidth?: number;
  /** 右边距 */
  marginRight?: number;
}
interface IInputItem extends IBaseItem {
  formItemType: 'Input';
  inputProps?: InputProps;
}
interface IInputNumberItem extends IBaseItem {
  formItemType: 'InputNumber';
  inputNumberProps?: InputNumberProps;
}
interface ICustomItem extends IBaseItem {
  formItemType: 'CustomItem';
  render?: ReactNode;
  disabledRender?: string;
}
interface ISelectItem extends IBaseItem {
  formItemType: 'Select';
  selectProps?: SelectProps;
  /** 枚举名称 */
  enumCode?: string;
}
export type combinationItemType = IInputItem | IInputNumberItem | ICustomItem | ISelectItem;
export interface SearchFormCombinationProps extends BaseSearchFormItemProps {
  /** 组合配置 */
  combinationItem?: combinationItemType[];
  /** 表单之间的分隔符 */
  splitSymbol?: string;
  /** 是否必传 */
  allRequired?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 日期之间分隔符  */
  middleText?: string;
}
/** 多个 Input/InputNumber/CustomItem 组合 */
export const SearchFormCombination: FC<SearchFormCombinationProps> = props => {
  const {
    combinationItem,
    isDetail,
    form,
    splitSymbol,
    isTable,
    testId,
    disabled,
    disabledRender,
    allRequired,
    required,
    hideLabel,
    label,
    middleText,
    className,
    name,
    ...resProps
  } = props;
  const { enumData } = useLayoutContext();
  if (!combinationItem) return null;
  const allFlex = arraySum(combinationItem.map(v => v.flex || 1));
  const averageWidth: number = 100 / allFlex;
  const renderChildren = (item: combinationItemType) => {
    switch (item.formItemType) {
      case 'CustomItem':
        return item.render;
      case 'InputNumber':
        return (
          <ProInputNumber
            style={{ width: '100%' }}
            company={item.company}
            companyWidth={item.companyWidth}
            {...item.inputNumberProps}
            placeholder={getInputPlaceholder(
              item.label,
              item.inputNumberProps,
              item.formItemProps?.rules,
            )}
          />
        );
      case 'Select':
        let options: any[] = [];
        if (item.enumCode && enumData) {
          options = enumData[item.enumCode]?.items ?? [];
        }
        return (
          <ProSelect
            style={{ width: '100%' }}
            options={options}
            {...item.selectProps}
            placeholder={getSelectPlaceholder(
              item.label,
              item.selectProps,
              item.formItemProps?.rules,
            )}
          />
        );
      default:
        return (
          <ProInput
            style={{ width: '100%' }}
            company={item.company}
            companyWidth={item.companyWidth}
            {...item.inputProps}
            placeholder={getInputPlaceholder(
              item.label,
              item.inputProps,
              item.formItemProps?.rules,
            )}
          />
        );
    }
  };
  const renderText = (): ReactNode => {
    if (disabledRender) {
      return typeof disabledRender === 'function' ? disabledRender() : disabledRender;
    }
    return combinationItem
      .map(item => {
        if (!item.formItemProps?.name && item.formItemType === 'CustomItem') {
          return item.disabledRender;
        }
        const val = form?.getFieldValue?.(item.formItemProps?.name as string);
        let resVal: any = TABLE_COLUMN_EMPTY;
        if (val !== undefined) {
          if (item.formItemType === 'Select') {
            let options: any[] = item.selectProps?.options ?? [];
            if (item.enumCode && enumData) {
              options = enumData[item.enumCode]?.items ?? [];
            }
            resVal = renderEnumName(val, options) || val;
            if (resVal) resVal = `${resVal}${item.company || ''}`;
            return item.label ? `${item.label}: ${resVal}` : resVal;
          }
          resVal = item.options ? renderEnumName(val, item.options) : val;
        }
        if (resVal) resVal = `${resVal}${item.company || ''}`;
        return item.label ? `${item.label}: ${resVal}` : resVal;
      })
      .join(splitSymbol);
  };
  const normFile = (e: any) => {
    if (typeof e === 'string') return e.trim();
    if (Array.isArray(e)) {
      const newValue: any[] = e.map(v => {
        if (typeof v === 'string') return v.trim();
        return v;
      });
      return newValue.filter(v => !!v);
    }
    if (e?.target) {
      return e?.target?.value;
    }
    return e;
  };
  const classes = classNames(className, {
    'address-required': allRequired || required,
  });
  return (
    <SearchFormItem
      {...resProps}
      className={classes}
      label={!hideLabel && label}
    >
      {isDetail || disabled ? (
        <SearchFormDisabled render={renderText} form={form} isTable={isTable} />
      ) : (
        <>
          {combinationItem.map((item, index) => {
            const itemClasses = classNames(item.formItemProps?.className, {
              'search-form-custom-item': item.formItemType === 'CustomItem',
            });
            const labelWidth = item.labelWidth || 32;
            let otherWidth: number = 0;
            if (item.label) otherWidth += labelWidth;
            if (hideLabel)
              otherWidth =
                otherWidth *
                ((combinationItem.length - 1) / combinationItem.length);
            return (
              <Fragment key={index}>
                {!(index === 0 && middleText) && item.label && (
                  <div
                    style={{
                      display: 'inline-block',
                      width: labelWidth,
                      height: 28,
                      lineHeight: '28px',
                      textAlign: 'center',
                      float: 'left',
                    }}
                  >
                    {middleText || item.label}
                  </div>
                )}
                <FormItem
                  key={index}
                  getValueFromEvent={normFile}
                  className={itemClasses}
                  style={{
                    display: 'inline-block',
                    width: `calc(${averageWidth *
                      (item.flex || 1)}% - ${otherWidth}px)`,
                    marginBottom: 0,
                    marginRight: 0,
                    textAlign: 'center',
                    float: 'left',
                  }}
                  {...item.formItemProps}
                  form={form}
                  hideLabel={true}
                  label={item.label}
                  test-id={`${testId}-${item.formItemProps?.name}`}

                >
                  {renderChildren(item)}
                </FormItem>
              </Fragment>
            );
          })}
        </>
      )}
    </SearchFormItem>
  );
};

SearchFormCombination.displayName = SearchFormItemEnum.SearchFormCombination;
SearchFormCombination.defaultProps = {
  splitSymbol: '，',
  middleText: '-',
};

export default SearchFormCombination;
